import { ExternalLink } from 'lucide-react';
import {
  useTranslation,
  type BillingPlanCatalogItem,
  type EntitlementOverage,
} from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { formatBillingPrice, overageLabelKey, planNameKey } from '../lib/billing-formatters';

export function ChangePlanDialog(props: {
  readonly open: boolean;
  readonly plan: BillingPlanCatalogItem | null;
  readonly overages: readonly EntitlementOverage[];
  readonly isPending: boolean;
  readonly approvalUrl: string | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  const { t, lang, dir } = useTranslation();

  return (
    <AlertDialog open={props.open} onOpenChange={props.onOpenChange}>
      <AlertDialogContent dir={dir}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.billing.dialog.changePlan.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {props.plan
              ? t('admin.billing.dialog.changePlan.planSummary', {
                  plan: t(planNameKey(props.plan.planKey)),
                  price: formatBillingPrice(props.plan.amountAgorot, lang),
                })
              : t('admin.billing.dialog.changePlan.overLimitDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {props.overages.length > 0 ? (
          <div className="border-saffron/40 bg-saffron/10 rounded-card border p-3 text-sm">
            <p>{t('admin.billing.dialog.changePlan.overLimitDescription')}</p>
            <ul className="mt-2 list-disc space-y-1 ps-5">
              {props.overages.map((overage) => (
                <li key={overage.gate}>{t(overageLabelKey(overage))}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {props.approvalUrl ? (
          <div className="border-zaatar/30 bg-zaatar-wash rounded-card border p-3 text-sm">
            <p className="text-sand-800">
              {t('admin.billing.dialog.changePlan.approvalDescription')}
            </p>
            <Button asChild className="mt-3 w-full sm:w-auto">
              <a href={props.approvalUrl} target="_blank" rel="noreferrer">
                {t('admin.billing.dialog.changePlan.openPayPalApproval')}
                <ExternalLink className="size-4" aria-hidden />
              </a>
            </Button>
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={props.isPending}>{t('common.cancel')}</AlertDialogCancel>
          {props.approvalUrl ? null : (
            <Button
              type="button"
              disabled={props.isPending || !props.plan}
              onClick={props.onConfirm}
            >
              {t('admin.billing.actions.confirmRevision')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
